import { Sector, PieSectorShapeProps } from 'recharts';

import { defaults } from './_constants';
import { buildSectorPath } from './_utils';

type PieShapeProps = PieSectorShapeProps & {
  gapWidth?: number;
  dataLength: number;
  isFullCircle: boolean;
};

/**
 * Custom sector shape with per-radius angular insets for straight,
 * uniform-width gaps and Recharts-compatible corner radius.
 */
export const PieShape = ({
  gapWidth = 0,
  dataLength,
  isFullCircle,
  ...sectorProps
}: PieShapeProps) => {
  const {
    startAngle,
    endAngle,
    innerRadius,
    outerRadius,
    cornerRadius = 0,
    index,
    fill,
    stroke,
    style,
  } = sectorProps;

  // No gap needed for a single sector or zero-value sectors
  if (dataLength <= 1 || startAngle === endAngle) {
    return <Sector {...sectorProps} />;
  }

  const strokeWidth = defaults.strokeWidth;
  const isFirst = index === 0;
  const isLast = index === dataLength - 1;
  const halfGap = gapWidth / 2 + (gapWidth > 0 ? strokeWidth / 2 : 0);
  const sign = startAngle > endAngle ? -1 : 1;

  const d = buildSectorPath({
    cx: sectorProps.cx,
    cy: sectorProps.cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    insetStartGap: isFirst && !isFullCircle ? 0 : halfGap,
    insetEndGap: isLast && !isFullCircle ? 0 : halfGap,
    cornerRadius,
    sign,
  });

  return (
    <path
      d={d}
      fill={style?.fill ?? fill}
      stroke={style?.stroke ?? stroke}
      strokeWidth={strokeWidth}
      style={{ outline: 'none' }}
    />
  );
};
